import React, { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard, Megaphone, BarChart3, LogOut, Menu, X, ChevronRight,
  User, Link2, Webhook, CreditCard,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import type { LucideIcon } from "lucide-react";

interface NavItem {
  name: string;
  icon: LucideIcon;
  href?: string;
  children?: { name: string; href: string }[];
}

const affiliateNav: NavItem[] = [
  { name: "Dashboard", icon: LayoutDashboard, href: "/portal/affiliate" },
  { name: "Offers", icon: Megaphone, href: "/portal/affiliate/offers" },
  {
    name: "Reporting", icon: BarChart3,
    children: [
      { name: "Clicks", href: "/portal/affiliate/reporting/clicks" },
      { name: "Conversions", href: "/portal/affiliate/reporting/conversions" },
    ],
  },
  { name: "Postbacks", icon: Webhook, href: "/portal/affiliate/postbacks" },
  { name: "Account", icon: User, href: "/portal/affiliate/account" },
];

const advertiserNav: NavItem[] = [
  { name: "Dashboard", icon: LayoutDashboard, href: "/portal/advertiser" },
  { name: "Offers", icon: Megaphone, href: "/portal/advertiser/offers" },
  {
    name: "Reporting", icon: BarChart3,
    children: [
      { name: "Clicks", href: "/portal/advertiser/reporting/clicks" },
      { name: "Conversions", href: "/portal/advertiser/reporting/conversions" },
    ],
  },
  { name: "Account", icon: User, href: "/portal/advertiser/account" },
];

const exactMatchPaths = new Set(["/portal/affiliate", "/portal/advertiser"]);

function isPathActive(href: string, currentPath: string): boolean {
  if (exactMatchPaths.has(href)) return currentPath === href;
  return currentPath.startsWith(href);
}

function NavGroup({ item, currentPath, onNavigate }: { item: NavItem; currentPath: string; onNavigate: () => void }) {
  const isChildActive = item.children?.some((c) => isPathActive(c.href, currentPath)) ?? false;
  const [open, setOpen] = useState(isChildActive);

  if (!item.children) {
    const active = isPathActive(item.href!, currentPath);
    return (
      <Link
        to={item.href!}
        onClick={onNavigate}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          active ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
        )}
      >
        <item.icon className="w-[18px] h-[18px] shrink-0" />
        {item.name}
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          isChildActive ? "text-gray-900" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
        )}
      >
        <item.icon className="w-[18px] h-[18px] shrink-0" />
        <span className="flex-1 text-left">{item.name}</span>
        <ChevronRight className={cn("w-4 h-4 text-gray-400 transition-transform duration-150", open && "rotate-90")} />
      </button>
      {open && (
        <div className="ml-4 pl-4 border-l border-gray-200 mt-0.5 mb-1 flex flex-col gap-0.5">
          {item.children.map((child) => {
            const active = isPathActive(child.href, currentPath);
            return (
              <Link
                key={child.href}
                to={child.href}
                onClick={onNavigate}
                className={cn(
                  "rounded-lg px-3 py-1.5 text-sm transition-colors",
                  active ? "bg-gray-100 text-gray-900 font-medium" : "text-gray-500 hover:bg-gray-50 hover:text-gray-900",
                )}
              >
                {child.name}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AffiliatePortalLayout({ children }: { children: React.ReactNode }) {
  return <PortalShell nav={affiliateNav} portalLabel="Affiliate Portal" accentColor="emerald">{children}</PortalShell>;
}

export function AdvertiserPortalLayout({ children }: { children: React.ReactNode }) {
  return <PortalShell nav={advertiserNav} portalLabel="Advertiser Portal" accentColor="blue">{children}</PortalShell>;
}

function PortalShell({ children, nav, portalLabel, accentColor }: {
  children: React.ReactNode; nav: NavItem[]; portalLabel: string; accentColor: "emerald" | "blue";
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const accentClasses = accentColor === "emerald"
    ? "bg-emerald-600 text-emerald-50"
    : "bg-blue-600 text-blue-50";

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-white border-r border-gray-200 transition-transform duration-200 lg:relative lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex items-center justify-between h-16 px-5 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <span className={cn("text-xs font-bold px-2 py-0.5 rounded", accentClasses)}>{accentColor === "emerald" ? "AFF" : "ADV"}</span>
            <span className="text-sm font-semibold tracking-tight text-gray-900">{portalLabel}</span>
          </div>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex flex-col gap-0.5 p-3 flex-1 overflow-y-auto">
          {nav.map((item) => (
            <NavGroup key={item.name} item={item} currentPath={currentPath} onNavigate={() => setSidebarOpen(false)} />
          ))}
        </nav>

        {user && (
          <div className="p-4 border-t border-gray-200 shrink-0">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0",
                accentColor === "emerald" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700",
              )}>
                {user.firstName?.[0]}{user.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.firstName} {user.lastName}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
              <button onClick={logout} className="text-gray-400 hover:text-gray-600 shrink-0" title="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </aside>

      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        <header className="flex items-center h-16 px-6 bg-white border-b border-gray-200 shrink-0">
          <button className="lg:hidden mr-4" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
        </header>
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
